import { makeAuthenticatedRequest } from './authService';

// Historical Event Interface
export interface HistoricalEvent {
  id: number;
  year: number;
  month: number | null;
  day: number | null;
  country: string;
  title: string;
  description: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateEventData {
  year: number;
  month?: number | null;
  day?: number | null;
  country: string;
  title: string;
  description?: string | null;
}

export interface UpdateEventData {
  year?: number;
  month?: number | null;
  day?: number | null;
  country?: string;
  title?: string;
  description?: string | null;
}

export interface GetEventsParams {
  year?: number;
  month?: number;
  day?: number;
  country?: string;
  limit?: number;
  offset?: number;
}

export interface AIGenerateEventsParams {
  startYear: number;
  endYear: number;
  country: string;
  count?: number;
}

export interface AIGeneratedEvent {
  year: number;
  month: number | null;
  day: number | null;
  country: string;
  title: string;
  description: string | null;
}

export const timeTunnelService = {
  /**
   * Get historical events with optional filters
   */
  getEvents: async (params?: GetEventsParams): Promise<{ success: boolean; events: HistoricalEvent[]; count: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.year) queryParams.append('year', params.year.toString());
    if (params?.month) queryParams.append('month', params.month.toString());
    if (params?.day) queryParams.append('day', params.day.toString());
    if (params?.country) queryParams.append('country', params.country);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const url = `/time-tunnel/events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return makeAuthenticatedRequest(url);
  },

  /**
   * Get a single event by ID
   */
  getEventById: async (id: number): Promise<{ success: boolean; event: HistoricalEvent }> => {
    return makeAuthenticatedRequest(`/time-tunnel/events/${id}`);
  },

  /**
   * Create a new historical event
   */
  createEvent: async (data: CreateEventData): Promise<{ success: boolean; id: number; message: string }> => {
    return makeAuthenticatedRequest('/time-tunnel/events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing event
   */
  updateEvent: async (id: number, data: UpdateEventData): Promise<{ success: boolean; message: string }> => {
    return makeAuthenticatedRequest(`/time-tunnel/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete an event
   */
  deleteEvent: async (id: number): Promise<{ success: boolean; message: string }> => {
    return makeAuthenticatedRequest(`/time-tunnel/events/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get list of years that have events
   */
  getYears: async (): Promise<{ success: boolean; years: number[] }> => {
    return makeAuthenticatedRequest('/time-tunnel/years');
  },

  /**
   * Get list of unique countries
   */
  getCountries: async (): Promise<{ success: boolean; countries: string[] }> => {
    return makeAuthenticatedRequest('/time-tunnel/countries');
  },

  /**
   * Generate historical events using AI
   */
  generateEventsWithAI: async (params: AIGenerateEventsParams): Promise<{ success: boolean; events: AIGeneratedEvent[] }> => {
    return makeAuthenticatedRequest('/time-tunnel/events/ai-generate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
};

